import {
    useEffect,
    useState,
    useContext,
    memo,
    useRef,
    useCallback,
} from "react";
import ReactECharts from "echarts-for-react";
import moment from "moment";

import { AppContext } from "context/appContext";
import { getOneRegistry } from "service/gatewayService";
import {
    getOnlineOfflineValue,
    fetchGatewayOnlineStatus,
} from "service/proqueryService";
import { convertMinutesToHours, isHttpSuccess } from "utils/functions";
import ChartBox from "../WidgetBox";
import useChartWidth from "hooks/useChartWidth";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import styled from "@emotion/styled";
import ChartByTime from "components/dashboard/ChartByTime";
import { ConsumptionChartBy } from "types";
import { DEFAULT_GATEWAY_NAME } from "constant/gateway";

const Status = styled.span<{ isOnline: boolean }>`
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 5px;

    background-color: ${({ isOnline }) => (isOnline ? "#219653" : "#F2495E")};
`;

const FilterWrapper = styled.div`
    z-index: 1;
    position: absolute;
    top: 53px;
    right: 16px;
`;

const Header = styled.div`
    z-index: 1;
    position: absolute;
    top: 44px;
    left: 16px;
    color: #828fab;
    font-size: 14px;
`;

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    position: absolute;
    bottom: 11px;
    left: 0;
    z-index: 1;
    color: #828fab;
    font-size: 13px;
    padding: 0 16px;
    font-weight: 500;
    width: 100%;
    gap: 10px;

    > div {
        flex: 1;
        white-space: nowrap;
    }

    .highlight {
        color: #00cbaf;
    }
`;

const StatusChart = (props: any) => {
    const {
        panel,
        panel: { chart, attributes, name },
        zoomLevel,
        allRegistry,
    } = props;
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
    const refreshRate = 30; // 60 seconds is the interval of gateway heartbeat report, but it's same as the timeout of Websocket, so we set fetch interval to 30 seconds to ensure the connection is alive.
    const thisGatewayId = attributes[0]?.gateway_id;
    const chartType = chart;
    const { storeData } = useContext(AppContext);
    const { organization } = storeData;
    const { currentOrgInfo } = organization;
    const { uuid: currOrgID, owner } = currentOrgInfo;
    const [gatewayName, setGatewayName] = useState("");
    const [uptime, setUptime] = useState({
        online: "0",
        offline: "0",
        onlinePercent: "0",
        offlinePercent: "0",
    });
    const [isLoading, setIsLoading] = useState(true);
    const [onlineStatus, setOnlineStatus] = useState<any>(null);

    const isOutOfPaymentToken = false;
    const gatewayId = panel.attributes[0].gateway_id;

    const boxElementRef = useRef<HTMLDivElement>(null);
    const { boxWidth } = useChartWidth({
        ref: boxElementRef,
        occupiedWidth: 80,
    });

    const gatewayDetail =
        (allRegistry ?? []).find((gw: any) => gw.gateway_id === gatewayId) ??
        {};

    if (gatewayId && onlineStatus?.[gatewayId]) {
        gatewayDetail.isOnline = onlineStatus[gatewayId].isOnline;
    }

    const fetchOnlineStatus = useCallback(async () => {
        if (!gatewayId) {
            return {};
        }
        const gatewayStatus = await fetchGatewayOnlineStatus([gatewayId]);
        return gatewayStatus;
    }, [gatewayId]);

    useEffect(() => {
        if (!gatewayId) {
            setOnlineStatus(null);
            return;
        }

        const fetchOnlineStatusInterval = async () => {
            try {
                const data = await fetchOnlineStatus();
                setOnlineStatus(data);
            } catch (error) {
                // Avoid unhandled promise rejection when polling fails.
                console.error("Failed to fetch gateway online status", error);
                setOnlineStatus(null);
            } finally {
                if (refreshRate) {
                    intervalIdRef.current = setTimeout(
                        fetchOnlineStatusInterval,
                        refreshRate * 1000
                    );
                }
            }
        };

        fetchOnlineStatusInterval();

        return () => {
            if (intervalIdRef.current) {
                clearTimeout(intervalIdRef.current);
            }
        };
    }, [fetchOnlineStatus, gatewayId]);

    useEffect(() => {
        if (!currOrgID || !owner) return;
        const fetch = async () => {
            const registryRes: any = await getOneRegistry(thisGatewayId);
            if (isHttpSuccess(registryRes.status)) {
                const { name } = registryRes.data;
                setGatewayName(name);
            }
        };
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panel, currOrgID, owner, thisGatewayId]);

    const fetchUptimeData = useCallback(
        async (fromTime: number, toTime: number) => {
            try {
                const onlineOfflineRes: any = await getOnlineOfflineValue(
                    thisGatewayId,
                    fromTime,
                    toTime
                );

                const totalDataPoints = (toTime - fromTime) / 60;

                const value =
                    onlineOfflineRes.data?.data?.result[0]?.value[1] || "0";
                const _value =
                    Number(value) > Number(totalDataPoints)
                        ? totalDataPoints
                        : value;

                const onlinePercent = (
                    (Number(_value) / totalDataPoints) *
                    100
                ).toFixed(2);
                setUptime({
                    online: convertMinutesToHours(Number(_value)),
                    offline: convertMinutesToHours(
                        Number(totalDataPoints) - Number(_value)
                    ),
                    onlinePercent,
                    offlinePercent: (100 - Number(onlinePercent)).toFixed(2),
                });
            } catch (error) {
                // Discuss to handle the OutOfPaymentToken case later
                // if (onlineOfflineRes.response?.status === FORBIDDEN) {
                //     if (
                //         onlineOfflineRes.config.url.includes(PANEL_TOKEN_ZERO_TOKEN)
                //     ) {
                //         setIsOutOfPaymentToken(true);
                //     }
                // }
            }
        },
        [thisGatewayId]
    );

    useEffect(() => {
        const fetch = async () => {
            await fetchUptimeData(
                moment().startOf("day").unix(),
                moment().endOf("day").unix()
            );

            setIsLoading(false);
        };

        if (isOutOfPaymentToken) {
            return;
        }

        fetch();
    }, [isOutOfPaymentToken, fetchUptimeData]);

    const option: any = {
        backgroundColor: "#2F3A51",
        responsive: true,
        maintainAspectRatio: false,
        tooltip: {
            trigger: "item",
        },
        title: {
            show: true,
            left: 8,
            top: 15,
            text: name || `${gatewayName} Status Chart`,
            textStyle: {
                color: "#ffffff",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 18,
                overflow: "truncate",
                ellipsis: "...",
                width: boxWidth,
            },
            subtext: ` `,
            subtextStyle: {
                color: "#828FAB",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 14,
                overflow: "truncate",
                ellipsis: "...",
                rich: {
                    s1: {
                        color: "#828fab",
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: 14,
                    },
                    s2: {
                        color: "#FC6464",
                        fontSize: 14,
                        fontFamily: "Roboto",
                        fontWeight: "bold",
                    },
                    s3: {
                        color: "#9A6363",
                        fontSize: 14,
                        fontWeight: "bold",
                        fontFamily: "Roboto",
                    },
                    s4: {
                        color: "#FFFFFF",
                        fontSize: 14,
                        fontWeight: "bold",
                        fontFamily: "Roboto",
                    },
                    s5: {
                        color: "#828fab",
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: 18,
                    },
                },
            },
        },
        legend: {
            show: false,
        },
        color: ["#00CBAF", "#7A86A0", "#3E4B67"],
        series: [
            {
                type: "pie",
                radius:
                    chartType.toUpperCase() === "PIE" ? "55%" : ["30%", "55%"],
                minAngle:
                    uptime.offline === "0" || uptime.online === "0" ? 0 : 5,
                top: 60,
                avoidLabelOverlap: true,
                label: {
                    formatter: (params: any) => {
                        const percent =
                            params.name === "Online"
                                ? uptime.onlinePercent
                                : uptime.offlinePercent;
                        return `{b|${params.name} -} {d|${percent}%}`;
                    },
                    show: true,
                    position: "outside",
                    fontSize: 12,
                    rich: {
                        b: {
                            lineHeight: 22,
                            align: "center",
                        },
                        d: {
                            lineHeight: 22,
                            align: "center",
                        },
                        e: {
                            lineHeight: 10,
                            align: "center",
                        },
                    },
                },
                labelLine: {
                    show: true,
                    length: 5,
                    length2: 20,
                    showAbove: true,
                },
                data: [
                    {
                        value: uptime.online,
                        name: "Online",
                    },
                    {
                        value: uptime.offline,
                        name: "Offline",
                    },
                ],
            },
        ],
    };

    const notEnoughPaymentTokenOption: any = {
        backgroundColor: "#2F3A51",
        responsive: true,
        maintainAspectRatio: false,
        tooltip: {
            trigger: "item",
        },
        title: {
            show: true,
            left: 8,
            top: 15,
            text: name || `${gatewayName} Status Chart`,
            textStyle: {
                color: "#ffffff",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 18,
                overflow: "truncate",
                ellipsis: "...",
            },
            subtext: `Online/Offline Status`,
            subtextStyle: {
                color: "#828FAB",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 14,
                overflow: "truncate",
                ellipsis: "...",
            },
        },
    };

    return (
        <ChartBox
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
            ref={boxElementRef}
        >
            <div className="widget">
                <Header>
                    <div>{gatewayDetail.name || DEFAULT_GATEWAY_NAME}</div>
                    <div>{gatewayDetail.gateway_id || ""}</div>
                    <div style={{ fontSize: 16, color: "#fff" }}>
                        <Status isOnline={!!gatewayDetail.isOnline} />
                        {gatewayDetail.isOnline ? "Online" : "Offline"}
                    </div>
                </Header>

                <FilterWrapper>
                    <ChartByTime
                        onChange={(t) => {
                            fetchUptimeData(t.fromTime.unix(), t.toTime.unix());
                        }}
                        view={ConsumptionChartBy.Day}
                    />
                </FilterWrapper>

                <Footer>
                    <div className="highlight">
                        Online - {uptime.onlinePercent}% ({uptime.online} Hours)
                    </div>
                    <div className="text-center">
                        Offline - {uptime.offlinePercent}% ({uptime.offline}{" "}
                        Hours)
                    </div>
                    <div></div>
                </Footer>

                <ContentWrapper isLoading={isLoading}>
                    <ReactECharts
                        className="chart-box"
                        option={
                            isOutOfPaymentToken
                                ? notEnoughPaymentTokenOption
                                : option
                        }
                        theme={"dark"}
                        notMerge={true}
                    />
                </ContentWrapper>
            </div>
        </ChartBox>
    );
};

export default memo(StatusChart);
